import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardTitle,
  CardBody,
  Col,
  Input,
  Row,
  Label,
  Button,
  FormGroup,
} from "reactstrap";
import { EditorState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { convertToHTML } from "draft-convert";
import Select from "react-select";
import { DatePicker } from "react-nice-dates";
import { sv } from "date-fns/locale";
import { FormattedMessage } from "react-intl";
import { PostNotification } from "../../actions/superUserMutations";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import moment from "moment";
import { fetchNotifications } from "../../actions/notificationActions";
import { useDispatch } from "react-redux";

export default function AdminNotificationsCreate({ schoolData, licenseData }) {
  const [notificationText, setNotificationText] = useState(null);
  const [notificationTitle, setNotificationTitle] = useState(null);
  const [notificationImage, setNotificationImage] = useState(null);
  const [notificationVideo, setNotificationVideo] = useState(null);
  const [forUserType, setForUserType] = useState(null);
  const [notificationDateForShow, setNotificationDateForShow] = useState(
    new Date()
  );

  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [licenseOptions, setLicenseOptions] = useState([]);
  const [mediaType, setMediaType] = useState("none");
  const [isIntrusive, setIsIntrusive] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [formKey, setFormKey] = useState(0);

  const licenseRef = useRef();
  const schoolRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    let tempSchoolOptions = [];
    let tempLicensOptions = [];

    schoolData.schoolResult.schools.forEach((school) => {
      let schoolOption = { value: school.id, label: school.name };
      tempSchoolOptions.push(schoolOption);
    });
    let allSchoolsOption = {
      value: "all",
      label: "Alla skolor",
    };
    tempSchoolOptions.unshift(allSchoolsOption);
    setSchoolOptions(tempSchoolOptions);
    licenseData.result.forEach((license) => {
      let licenseOption = { value: license.id, label: license.programName };
      tempLicensOptions.push(licenseOption);
    });
    let allLicensesOption = {
      value: "all",
      label: "Alla programlicenser",
    };
    tempLicensOptions.unshift(allLicensesOption);
    setLicenseOptions(tempLicensOptions);
  }, [schoolData, licenseData]);

  useEffect(() => {
    let html = convertToHTML(editorState.getCurrentContent());
    setNotificationText(html);
  }, [editorState]);

  const handlePostNotification = useMutation(PostNotification);

  const handleMediaSelect = () => {
    let retVal = { image: null, video: null };
    if (mediaType === "image") {
      retVal.image = notificationImage;
      retVal.video = null;
    } else if (mediaType === "video") {
      retVal.video = notificationVideo;
      retVal.Image = null;
    } else {
      retVal.video = null;
      retVal.image = null;
    }
    return retVal;
  };

  const handleLicensesSelect = () => {
    let selectedLicenses = licenseRef.current.select.getValue();
    let retVal = [];
    if (selectedLicenses.some((lic) => lic.value === "all")) {
      licenseData.result.forEach((license) => {
        retVal.push(license.id);
      });
    } else {
      selectedLicenses.forEach((selLic) => {
        retVal.push(selLic.value);
      });
    }
    return retVal;
  };

  const handleSelectForUserType = (userType) => {
    setForUserType(userType);
    setValidationErrors(validationErrors.filter((val) => val !== "userType"));
  };

  const handlePost = () => {
    let media = handleMediaSelect();
    let licenses = handleLicensesSelect();
    let forAllSchools = schoolRef.current.select
      .getValue()
      .some((val) => val.value === "all");
    let schoolIds = [];
    let selectedSchools = schoolRef.current.select.getValue();
    selectedSchools.forEach((school) => {
      schoolIds.push(school.value);
    });
    let postObject = {
      title: notificationTitle,
      text: notificationText,
      image: media.image,
      video: media.video,
      forLicenses: licenses,
      forSchools: forAllSchools ? [] : schoolIds,
      showForTeachers: forUserType === "teachers" || forUserType === "all",
      showForStudents: forUserType === "students" || forUserType === "all",
      showForAllCustomers: forAllSchools,
      showFrom: moment(notificationDateForShow).format("YYYY-MM-DD"),
      isIntrusive: isIntrusive,
    };
    let error = false;
    let tempErrors = [...validationErrors];
    if (
      schoolRef.current.select.getValue() === null ||
      schoolRef.current.select.getValue().length < 1
    ) {
      tempErrors = [...tempErrors, "schools"];
      error = true;
    } else {
      tempErrors = tempErrors.filter((val) => val !== "schools");
    }
    if (forUserType === null) {
      tempErrors = [...tempErrors, "userType"];
      error = true;
    } else {
      tempErrors = tempErrors.filter((val) => val !== "userType");
    }
    if (licenses.length < 1) {
      tempErrors = [...tempErrors, "licenses"];
      error = true;
    } else {
      tempErrors = tempErrors.filter((val) => val !== "licenses");
    }
    setValidationErrors(tempErrors);

    if (!error) {
      handlePostNotification.mutate(
        {
          postObject,
        },
        {
          onSuccess: (data) => {
            if (data && data.status > 199 && data.status < 300) {
              toast.success("Notis postad");
              dispatch(fetchNotifications());
              setFormKey((prev) => prev + 1);
              setNotificationText(null);
              setNotificationTitle(null);
              setNotificationImage(null);
              setNotificationVideo(null);
              setForUserType(null);
              setNotificationDateForShow(new Date());
              setEditorState(EditorState.createEmpty());
              setMediaType("none");
              setIsIntrusive(false);
              setValidationErrors([]);
            } else {
              toast.warn(data);
            }
          },
          throwOnError: true,
          onError: (data) => {
            toast.warn(data);
          },
        }
      );
    }
  };

  const mediaChooser = () => {
    if (mediaType === "image") {
      return (
        <Input
          type="file"
          onChange={(data) => setNotificationImage(data.target.files[0])}
        />
      );
    } else if (mediaType === "video") {
      return (
        <Input
          type="text"
          placeholder="ex. https://www.youtube.com/watch?v=dQw4w9WgXcQ"
          onChange={(data) => setNotificationVideo(data.target.value)}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Card key={formKey} style={{ zIndex: "9" }}>
      <CardBody>
        <CardTitle>
          <FormattedMessage id="CreateNewNotification" />
        </CardTitle>
        <Row>
          <Col lg="5">
            <p className="lead" style={{ marginBottom: ".3rem" }}>
              <FormattedMessage id="Content" />
            </p>
            <hr style={{ marginTop: ".3rem" }} />
          </Col>
        </Row>
        <Row>
          <Col lg="4">
            <Input
              placeholder="Rubrik"
              name="Title"
              type="text"
              onChange={(data) => setNotificationTitle(data.target.value)}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col lg="6">
            <Editor
              editorState={editorState}
              placeholder="Text"
              toolbar={{
                options: [
                  "inline",
                  "blockType",
                  "fontSize",
                  "list",
                  "textAlign",
                  "history",
                ],
              }}
              toolbarClassName="notifications--editor--Toolbar"
              wrapperClassName="notifications--editor--Wrapper"
              editorClassName="notifications--editor--Input"
              onEditorStateChange={setEditorState}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col sm="2">
            <FormGroup tag="fieldset">
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="mediaTypeRadio"
                    defaultChecked={true}
                    onChange={() => setMediaType("none")}
                  />{" "}
                  <FormattedMessage id="None" />
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="mediaTypeRadio"
                    onChange={() => setMediaType("image")}
                  />{" "}
                  <FormattedMessage id="Image" />
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="mediaTypeRadio"
                    onChange={() => setMediaType("video")}
                  />{" "}
                  <FormattedMessage id="Video" />
                </Label>
              </FormGroup>
            </FormGroup>
          </Col>
          <Col>{mediaChooser()}</Col>
        </Row>

        <Row>
          <Col lg="5">
            <p className="lead" style={{ marginBottom: ".3rem" }}>
              <FormattedMessage id="Display" />
            </p>
            <hr style={{ marginTop: ".3rem" }} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Label for="date">
              <FormattedMessage id="FromWhatDateShouldNotificationBeVisible" />?
            </Label>
            <DatePicker
              name="date"
              date={notificationDateForShow}
              onDateChange={(value) => setNotificationDateForShow(value)}
              locale={sv}
              format="yyyy-MM-dd"
            >
              {({ inputProps, focused }) => (
                <input
                  className={
                    "input" +
                    (focused ? " -focused" : "") +
                    " " +
                    "date--picker"
                  }
                  {...inputProps}
                />
              )}
            </DatePicker>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col>
            <Label for="schools">
              <FormattedMessage id="ForWhichSchoolShouldNotificationBeVisible" />
              ?
            </Label>
            <Select
              classNamePrefix={
                validationErrors.some((val) => val === "schools")
                  ? "react-select--error"
                  : ""
              }
              name="schools"
              ref={schoolRef}
              onChange={() =>
                setValidationErrors(
                  validationErrors.filter((val) => val !== "schools")
                )
              }
              placeholder="Välj skola/skolor"
              isMulti
              options={schoolOptions}
            />
            {validationErrors.some((val) => val === "schools") ? (
              <Label for="schools" style={{ color: "red" }}>
                <FormattedMessage id="YouHaveToSelectSchoolForNotification" />.
              </Label>
            ) : null}
          </Col>
          <Col>
            <Label for="licenses">
              <FormattedMessage id="ForWhichProgramlicensesShouldNotificationBeVisible" />
              ?
            </Label>
            <Select
              classNamePrefix={
                validationErrors.some((val) => val === "licenses")
                  ? "react-select--error"
                  : ""
              }
              name="licenses"
              ref={licenseRef}
              placeholder="Välj programlicenser"
              isMulti
              options={licenseOptions}
              onChange={() =>
                setValidationErrors(
                  validationErrors.filter((val) => val !== "licenses")
                )
              }
            />
            {validationErrors.some((val) => val === "licenses") && (
              <Label for="licenses" style={{ color: "red" }}>
                <FormattedMessage id="YouHaveToSelectLicensesForNotification" />
                .
              </Label>
            )}
          </Col>
          <Col>
            <Label for="userTypes">
              <FormattedMessage id="ForWhichsertypesShouldNotificationBeVisible" />
              ?
            </Label>
            <Select
              name="userTypes"
              classNamePrefix={
                validationErrors.some((val) => val === "userType")
                  ? "react-select--error"
                  : ""
              }
              placeholder="Välj användare"
              onChange={(val) => handleSelectForUserType(val.value)}
              options={[
                { value: "all", label: "Alla" },
                { value: "teachers", label: "Lärare" },
                { value: "students", label: "Elever" },
              ]}
            />
            {validationErrors.some((val) => val === "userType") && (
              <Label for="userTypes" style={{ color: "red" }}>
                <FormattedMessage id="YouHaveToSelectUsersForNotification" />.
              </Label>
            )}
          </Col>
        </Row>

        <Row className="mt-2">
          <Col lg="4">
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  checked={isIntrusive}
                  onChange={() => setIsIntrusive(!isIntrusive)}
                />{" "}
                <FormattedMessage id="IntrusiveNotification" />
              </Label>
            </FormGroup>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col lg="3">
            <Button color={"primary"} outline onClick={() => handlePost()}>
              {" "}
              <FormattedMessage id="PostNotification" />
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
